import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../components/sideBar";

interface Tab {
  label: string;
  key: string;
  content: React.ReactNode;
}

interface LayoutProps {
  pageTitle: string;
  tabs?: Tab[];
  children?: React.ReactNode;
  eventId?: any; // ✅ Event ID for Search
  actionButton?: {
    label: string;
    onClick: () => void;
    className?: string;
    icon?: React.ReactNode;
  }; // ✅ Dynamic Action Button
  searchByEventId?: {
    label: string;
    onChange?: (val: any) => void | undefined;
    onClick: (val?: any) => void;
    className?: string;
  }; // ✅ Search by Event ID Textbox
}

export default function Layout({
  children,
  pageTitle,
  tabs,
  actionButton,
  searchByEventId,
  eventId,
}: LayoutProps) {
  const [activeTab, setActiveTab] = useState(tabs?.[0]?.key || "");
  const [inputValue, setInputValue] = useState<any>(eventId || "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // expose a global custom event listener to focus input from child
  useEffect(() => {
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    window.addEventListener("focusSearchInput", focusInput);
    return () => window.removeEventListener("focusSearchInput", focusInput);
  }, []);
  return (
    <div className="flex min-h-screen bg-gray-100 overflow-y-hidden">
      {/* Sidebar */}
      <div className="h-screen flex-shrink-0">
        <Sidebar />
      </div>

      {/* Main Area */}
      <div className="flex flex-col flex-1 min-w-0 h-screen overflow-hidden">
        {/* Header */}
        <header className="border-b bg-white py-3.5 px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold text-gray-800">{pageTitle}</h1>
          </div>

          {/* Right-side container always exists */}
          <div className="flex items-center justify-end min-h-[40px]">
            {actionButton && (
              <button
                onClick={actionButton.onClick}
                className={`p-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition ${
                  actionButton.className || ""
                }`}
              >
                {actionButton.icon && (
                  <span className="inline-block mr-2">{actionButton.icon}</span>
                )}
                {actionButton.label}
              </button>
            )}

            {searchByEventId && (
              <div className="relative w-80 mx-3">
                {/* Inline Label */}
                <span className="absolute -top-2 left-3 bg-white px-1 text-xs text-gray-600">
                  {searchByEventId.label || "Search Event ID"}
                </span>

                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={inputValue}
                  onChange={(e) => {
                    // Allow only digits
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setInputValue(numericValue);
                  }}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Enter",
                    ];

                    // ✅ Allow paste (Ctrl+V / Cmd+V)
                    if (
                      (e.ctrlKey || e.metaKey) &&
                      e.key.toLowerCase() === "v"
                    ) {
                      return;
                    }

                    // Block non-digits
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      !allowedKeys.includes(e.key)
                    ) {
                      e.preventDefault();
                    }

                    if (e.key === "Enter") {
                      searchByEventId?.onClick(inputValue.trim());

                      const el = e.currentTarget;
                      el.classList.add("dark-border");
                      setTimeout(() => el.classList.remove("dark-border"), 600);
                    }
                  }}
                  className={`
    border rounded-lg px-3 py-2.5 pr-24 w-full
    focus:outline-none transition-all
    border-green-300 focus:border-green-300
  `}
                />

                {/* ✕ Clear */}
                {inputValue && (
                  <button
                    onClick={() => {
                      setInputValue("");
                      searchByEventId?.onClick("");
                    }}
                    className="absolute right-16 top-1/2 -translate-y-1/2 
                   text-gray-400 hover:text-red-500 text-base"
                    title="Clear"
                  >
                    ✕
                  </button>
                )}

                {/* 🔍 Search */}
                <button
                  onClick={() => {
                    searchByEventId?.onClick(inputValue.trim());

                    // manually trigger dark border on click
                    const el = inputRef.current;
                    if (el) {
                      el.classList.add("dark-border");
                      setTimeout(() => el.classList.remove("dark-border"), 600);
                    }
                  }}
                  disabled={!inputValue.trim()}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-md 
        text-white text-sm transition
        ${
          inputValue.trim()
            ? "bg-green-600 hover:bg-green-700"
            : "bg-gray-300 cursor-not-allowed"
        }`}
                  title="Search"
                >
                  🔍
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Tabs */}
        {tabs && tabs.length > 0 && (
          <div className="flex space-x-2 bg-white px-4 border-b overflow-x-auto">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-3 px-5 text-sm font-medium rounded-t-lg transition-all duration-200 whitespace-nowrap
                    ${
                      isActive
                        ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                        : "text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-b-2 border-transparent"
                    }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="overflow-x-auto">
            {tabs && tabs.length > 0
              ? tabs.find((t) => t.key === activeTab)?.content
              : children}
          </div>
        </main>
      </div>
    </div>
  );
}
